/**
 * viewportClipObjectsStrip — DATA-INTEGRITY round-trip cleanup for an INVALIDATED
 * viewport clip whose boundary came from the OBJECTS
 * ext-dict → ACAD_FILTER → SPATIAL → SPATIAL_FILTER chain (Codex #326 r3 P2 #3).
 *
 * THE BUG it fixes. A non-rectangular VIEWPORT clip sourced from a SPATIAL_FILTER
 * (XCLIP-style) is persisted as TWO co-operating artefacts:
 *
 *   1. the VIEWPORT entity's `extensionDictionaryHandle` (DXF 360) — the anchor
 *      `resolveViewportClip` walks: ext-dict DICTIONARY → `ACAD_FILTER` sub-dict →
 *      `SPATIAL` entry → SPATIAL_FILTER record; and
 *   2. the OBJECTS records themselves (the ext-dict DICTIONARY, the ACAD_FILTER
 *      filter-dict, the SPATIAL_FILTER), carried verbatim on the DrawingDocument
 *      as the flat `rawObjectGroups` stream and re-emitted byte-for-byte on export.
 *
 * `invalidateViewportClip` (command-engine) drops the DERIVED render polygon
 * (`clipPath`) and the native group-340 carriers — but it operates on a single
 * entity and CANNOT reach the document-scope OBJECTS chain. So a SPATIAL_FILTER-
 * sourced clip, once "deleted", still ships the ext-dict→ACAD_FILTER→SPATIAL_FILTER
 * chain on export; reopening the saved DXF re-runs `resolveViewportClip`, the chain
 * re-resolves, and the "removed" clip is RESTORED. The clip does not stay deleted
 * across save+reload — a data-integrity regression.
 *
 * THE FIX. After a clip is invalidated, also DETACH the persisted chain at the
 * document level: for each invalidated viewport's ext-dict DICTIONARY record (the
 * one whose own `5/<handle>` equals the viewport's `extensionDictionaryHandle`),
 * remove ONLY the `3/ACAD_FILTER` entry + its following `350/360/<filter-dict>`
 * pointer (every OTHER entry in that dictionary — an annotation-scale XRECORD, a
 * vendor XDICTIONARY entry, … — is PRESERVED). Then strip the now-orphaned
 * filter-dict record and the SPATIAL_FILTER record it points at so no dangling
 * OBJECTS remain. With the `ACAD_FILTER` reference gone, reopening the DXF can no
 * longer reach a SPATIAL_FILTER from the viewport, so the deleted clip stays
 * deleted — while the viewport's `extensionDictionaryHandle` (and any unrelated
 * ext-dict content) round-trips intact.
 *
 * Mirrors the verbatim `rawObjectGroups` record-boundary surgery already used by
 * `dxfBridge.annotativeExport.stripPreservedScaleListDict`: the stream is a series
 * of records, each opened by a `0/<TYPE>` group and running to the next `0/`.
 *
 * Pure: every function returns a NEW array, never mutates the input.
 */

import type { DrawingDocument, ViewportEntity } from "./model";

type RawGroup = { code: number; value: string };

/** Case-insensitive hex-handle compare (DXF handles are case-insensitive). */
function sameHandle(a: string | undefined, b: string | undefined): boolean {
  if (a === undefined || b === undefined) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Find the index range `[start, end)` of the record whose opening `0/<type>` is at
 * `openerIdx` — i.e. from `openerIdx` up to (but not including) the next `0/`
 * group or the end of the stream.
 */
function recordEnd(groups: ReadonlyArray<RawGroup>, openerIdx: number): number {
  let k = openerIdx + 1;
  const n = groups.length;
  while (k < n && groups[k]!.code !== 0) k++;
  return k;
}

/** Read a record's own handle (the first `5/` before the next `0/`). */
function recordHandle(
  groups: ReadonlyArray<RawGroup>,
  openerIdx: number,
  end: number,
): string | undefined {
  for (let j = openerIdx + 1; j < end; j++) {
    if (groups[j]!.code === 5) return groups[j]!.value.trim();
  }
  return undefined;
}

const DICT_OPENERS = new Set(["DICTIONARY", "ACDBDICTIONARYWDFLT"]);

/**
 * Within a DICTIONARY record's `[start, end)` range, locate the `3/<entryName>`
 * entry (case-sensitive — DXF dictionary entry names like `ACAD_FILTER` are exact
 * identifiers) and return both its `3/` index and the index of its following
 * `350/360/<handle>` pointer (the entry value), plus the pointed-at handle.
 * Returns undefined when the entry is absent.
 */
function findDictEntry(
  groups: ReadonlyArray<RawGroup>,
  start: number,
  end: number,
  entryName: string,
): { nameIdx: number; ptrIdx: number; handle: string } | undefined {
  for (let j = start; j < end; j++) {
    const g = groups[j]!;
    if (g.code === 3 && g.value.trim() === entryName) {
      // The pointer is the next 350 (soft) / 360 (hard) before another 3/ or 0/.
      for (let k = j + 1; k < end; k++) {
        const gk = groups[k]!;
        if (gk.code === 350 || gk.code === 360) {
          return { nameIdx: j, ptrIdx: k, handle: gk.value.trim() };
        }
        if (gk.code === 3) break; // next entry — this one had no pointer
      }
      return undefined;
    }
  }
  return undefined;
}

/**
 * Locate a DICTIONARY / ACDBDICTIONARYWDFLT record by its OWN handle (group 5),
 * scanning the flat OBJECTS stream record-by-record. The SINGLE "walk records,
 * match a DICTIONARY by handle" primitive shared by every dictionary-chain
 * walker in this module (`resolveClipChain`, `resolveSpatialHandle`,
 * `attachAcadFilterEntry`) — extracted so the three sites cannot drift on the
 * record-boundary scan (jscpd — the three inlined copies were a byte-identical
 * 9-line clone). Returns the record's `[start, end)` index range, or `undefined`
 * when no DICTIONARY record with that handle exists.
 */
function findDictionaryRecordByHandle(
  groups: ReadonlyArray<RawGroup>,
  handle: string,
): { start: number; end: number } | undefined {
  const n = groups.length;
  let i = 0;
  while (i < n) {
    const g = groups[i]!;
    if (g.code === 0 && DICT_OPENERS.has(g.value)) {
      const end = recordEnd(groups, i);
      if (sameHandle(recordHandle(groups, i, end), handle)) {
        return { start: i, end };
      }
      i = end;
      continue;
    }
    i++;
  }
  return undefined;
}

/**
 * Resolve, from the flat OBJECTS stream, the clip chain reachable from a viewport
 * ext-dictionary handle: the ext-dict record range, its `ACAD_FILTER` entry
 * (name + pointer indices), the filter-dict's `SPATIAL` → SPATIAL_FILTER handle.
 * Returns undefined when the viewport's ext-dict carries no ACAD_FILTER entry (no
 * SPATIAL_FILTER-sourced clip to strip — e.g. an ext-dict that only holds an
 * annotation-scale XRECORD).
 */
function resolveClipChain(
  groups: ReadonlyArray<RawGroup>,
  extDictHandle: string,
): {
  extDictStart: number;
  extDictEnd: number;
  acadFilterNameIdx: number;
  acadFilterPtrIdx: number;
  filterDictHandle: string;
  spatialFilterHandle: string | undefined;
} | undefined {
  const extDict = findDictionaryRecordByHandle(groups, extDictHandle);
  if (!extDict) return undefined;
  const acadFilter = findDictEntry(
    groups,
    extDict.start + 1,
    extDict.end,
    "ACAD_FILTER",
  );
  if (!acadFilter) return undefined; // no clip chain on this ext-dict
  // Walk filter-dict → SPATIAL entry → SPATIAL_FILTER handle.
  const spatialFilterHandle = resolveSpatialHandle(groups, acadFilter.handle);
  return {
    extDictStart: extDict.start,
    extDictEnd: extDict.end,
    acadFilterNameIdx: acadFilter.nameIdx,
    acadFilterPtrIdx: acadFilter.ptrIdx,
    filterDictHandle: acadFilter.handle,
    spatialFilterHandle,
  };
}

/**
 * Given the filter-dict handle, find its DICTIONARY record and read its `SPATIAL`
 * entry's pointer — the SPATIAL_FILTER handle. Returns undefined when the filter
 * dict / SPATIAL entry is absent (the SPATIAL_FILTER record then cannot be matched
 * by handle, so it is left as-is — detaching the ACAD_FILTER reference alone still
 * stops re-resolution).
 */
function resolveSpatialHandle(
  groups: ReadonlyArray<RawGroup>,
  filterDictHandle: string,
): string | undefined {
  const filterDict = findDictionaryRecordByHandle(groups, filterDictHandle);
  if (!filterDict) return undefined;
  const spatial = findDictEntry(
    groups,
    filterDict.start + 1,
    filterDict.end,
    "SPATIAL",
  );
  return spatial?.handle;
}

/**
 * Strip the SPATIAL_FILTER-sourced clip chain reachable from ONE viewport
 * ext-dict handle out of a `rawObjectGroups` stream:
 *   - remove ONLY the `3/ACAD_FILTER` + its `350/360` pointer from the ext-dict
 *     (preserving every other entry of that dictionary);
 *   - drop the orphaned filter-dict record;
 *   - drop the SPATIAL_FILTER record the filter-dict pointed at.
 * Returns the SAME reference when there is nothing to strip (no ACAD_FILTER entry).
 */
function stripOneViewportClipChain(
  groups: ReadonlyArray<RawGroup>,
  extDictHandle: string,
): ReadonlyArray<RawGroup> {
  const chain = resolveClipChain(groups, extDictHandle);
  if (!chain) return groups;

  // Handles of the whole-record DICTIONARY (filter-dict) + SPATIAL_FILTER to drop.
  const dropRecordHandles = new Set<string>();
  dropRecordHandles.add(chain.filterDictHandle.toLowerCase());
  if (chain.spatialFilterHandle) {
    dropRecordHandles.add(chain.spatialFilterHandle.toLowerCase());
  }

  // Indices INSIDE the ext-dict to remove (the ACAD_FILTER entry name + pointer).
  const dropEntryIdx = new Set<number>([
    chain.acadFilterNameIdx,
    chain.acadFilterPtrIdx,
  ]);

  const out: RawGroup[] = [];
  const n = groups.length;
  let i = 0;
  while (i < n) {
    const g = groups[i]!;
    if (g.code === 0) {
      const end = recordEnd(groups, i);
      // Drop the orphaned filter-dict / SPATIAL_FILTER whole records by handle.
      const h = recordHandle(groups, i, end);
      if (h && dropRecordHandles.has(h.toLowerCase())) {
        i = end;
        continue;
      }
      // Otherwise copy the record, dropping the ACAD_FILTER entry pair if this is
      // the ext-dict record.
      for (let j = i; j < end; j++) {
        if (dropEntryIdx.has(j)) continue;
        out.push(groups[j]!);
      }
      i = end;
      continue;
    }
    // Stray top-level group (no enclosing record) — copy verbatim.
    out.push(g);
    i++;
  }
  return out;
}

/**
 * Detach the persisted SPATIAL_FILTER clip chain for every viewport in
 * `invalidatedViewports` from a document's verbatim OBJECTS round-trip
 * (`rawObjectGroups`), so a clip invalidated in the editor STAYS deleted across
 * save+reload (Codex #326 r3 P2 #3). Each viewport contributes its
 * `extensionDictionaryHandle` anchor; viewports without one (or whose ext-dict has
 * no ACAD_FILTER entry — e.g. a native group-340 clip, already handled entity-side)
 * are no-ops. Returns the SAME document reference when nothing changed.
 *
 * Pure: never mutates the input document (returns a shallow clone with a new
 * `rawObjectGroups` only when a chain was actually stripped).
 */
export function detachInvalidatedViewportClipObjects(
  document: DrawingDocument,
  invalidatedViewports: ReadonlyArray<ViewportEntity>,
): DrawingDocument {
  const raw = document.rawObjectGroups;
  if (!raw || raw.length === 0 || invalidatedViewports.length === 0) {
    return document;
  }
  let current: ReadonlyArray<RawGroup> = raw;
  for (const vp of invalidatedViewports) {
    const extDict = vp.extensionDictionaryHandle;
    if (!extDict) continue;
    current = stripOneViewportClipChain(current, extDict);
  }
  if (current === raw) return document;
  return {
    ...document,
    rawObjectGroups: current as RawGroup[],
  };
}

/**
 * Attach a `3/ACAD_FILTER + 350/<acadFilterDictHandle>` entry pair onto an
 * EXISTING extension-dictionary DICTIONARY record (own handle ===
 * `extDictHandle`), preserving every other entry / nested block verbatim — the
 * INVERSE surgical operation of `stripOneViewportClipChain`'s ACAD_FILTER
 * removal above. Used by the N7 VPCLIP-BOUNDARY-EXPORT synthesis
 * (`viewportClipObjectsSynthesize.ts`) to re-attach a FRESH SPATIAL_FILTER
 * chain onto a viewport ext-dict that survived a PRIOR clip invalidation (its
 * old ACAD_FILTER entry was already stripped by `detachInvalidatedViewport
 * ClipObjects` on re-clip), or that carries unrelated content (e.g. an
 * annotation-scale XRECORD) the entity's ext-dict already owns.
 *
 * The new entry is inserted immediately BEFORE the record's first XDATA group
 * (code 1001), if any — DXF requires XDATA to trail a record's real groups, so
 * appending unconditionally at the record's end could re-order it after XDATA
 * on an ext-dict record that happens to carry some. A record without XDATA
 * simply gets the pair appended at its end (mirrors `serializeDictionaryEntry`'s
 * own canonical emit order: entries before xdata).
 *
 * Returns `{ attached: false, rawObjectGroups: groups }` (the SAME reference)
 * when the ext-dict record cannot be found by handle, or it ALREADY carries an
 * `ACAD_FILTER` entry — the synthesis gate (`needsClipSynthesis`) should make
 * the latter unreachable in practice; this is a defensive guard that never
 * overwrites / duplicates an existing entry rather than risk corrupting an
 * unknown structure.
 */
export function attachAcadFilterEntry(
  groups: ReadonlyArray<RawGroup>,
  extDictHandle: string,
  acadFilterDictHandle: string,
): { attached: boolean; rawObjectGroups: RawGroup[] } {
  const extDict = findDictionaryRecordByHandle(groups, extDictHandle);
  if (!extDict) {
    return { attached: false, rawObjectGroups: groups as RawGroup[] };
  }
  const { start, end } = extDict;
  if (findDictEntry(groups, start + 1, end, "ACAD_FILTER")) {
    return { attached: false, rawObjectGroups: groups as RawGroup[] };
  }
  let insertAt = end;
  for (let j = start + 1; j < end; j++) {
    if (groups[j]!.code === 1001) {
      insertAt = j;
      break;
    }
  }
  const out: RawGroup[] = [
    ...groups.slice(0, insertAt),
    { code: 3, value: "ACAD_FILTER" },
    { code: 350, value: acadFilterDictHandle },
    ...groups.slice(insertAt),
  ];
  return { attached: true, rawObjectGroups: out };
}
